/**
 * Sentry configuration
 * This file contains configuration for Sentry error tracking
 */

/**
 * List of errors to ignore in Sentry reporting
 * Add patterns for errors that should not be reported to Sentry
 */
const SENTRY_IGNORE_ERRORS = [
  // Internal library errors
  "_abt/*init*/(arg[0]='isArray')",
  // Client script errors (not from our app)
  "TypeError: Cannot read properties of null (reading 'contentWindow')",
  // Is logged to AWS ALB
  "Opened not found page with status 404.",
  // Overall app errors that are not critical
  "write after end",
  // Happens on runners
  "Error - unable to load fileServer storage data. Maybe you have incorrect url?",
  "Error - unable to load fileServer storage data.",
  // GTM script zaletsi (not from our app)
  "No error message",
  "Can't find variable: _AutofillCallbackHandler",
  // Recommendation to filter out (fixed in newer version) https://github.com/getsentry/sentry-javascript/issues/15065
  "Java exception was raised during method invocation",
  // Client script errors (not from our app)
  "after argument list",
  "Non-Error promise rejection captured with value: Object Not Found Matching Id:",
  "(evaluating 'iFrame.contentWindow')",
  "iFrame is null",
  "Cannot read properties of undefined (reading '$el')",
  "t.$_Tawk.i18next",
  "Cannot read properties of undefined (reading 'clientHeight')",
  "ResizeObserver loop completed with undelivered notifications.",
  "Non-Error exception captured with keys: currentTarget, isTrusted, target, type",
  "Non-Error promise rejection captured with keys: currentTarget, detail, isTrusted, target",
  "Cannot read properties of undefined (reading 'touches')",
  "TypeError: undefined is not an object (evaluating 'activeTab.id')",
  "is not a valid selector",
  // Footer errors
  "AbortError: Share canceled",
  "ResizeObserver loop completed with undelivered notifications.",
  // tztraveltrack.golibe.com footer script error
  "Cannot read properties of undefined (reading '$el')",
  "t.$_Tawk.i18next",
  "Cannot read properties of undefined (reading 'clientHeight')",
  // manntravelau.golibe.com site error - catched by our iFrame
  "Non-Error promise rejection captured with value: Object Not Found Matching Id:",
  "(evaluating 'iFrame.contentWindow')",
  "iFrame is null",
  // Recommendation to filter out (fixed in newer version) https://github.com/getsentry/sentry-javascript/issues/15065
  "Java exception was raised during method invocation",
  "BufferLoader: XHR error",
  "Cannot read properties of null (reading 'touches')",
  "read properties of null (reading 'shadowRoot')",
  "undefined is not an object (evaluating 'window.webkit.messageHandlers[t].postMessage')",
  "$a",
];

const SENTRY_IGNORE_ERROR_SOURCES = ["iabjs://browser_declutter"];

const GOOGLE_MAPS_API_SOURCE = "maps.googleapis.com/maps-api-v3/";
const GOOGLE_MAPS_MODULE_LOAD_ERRORS = new Set([
  'Could not load "util".',
  'Could not load "map".',
]);

const isIgnoredErrorSource = (source) =>
  SENTRY_IGNORE_ERROR_SOURCES.includes(source);

const isGoogleMapsApiSource = (source) =>
  typeof source === "string" &&
  (source.includes(GOOGLE_MAPS_API_SOURCE) ||
    source.startsWith("/maps-api-v3/"));

const isIgnoredGoogleMapsModuleError = (exception) =>
  GOOGLE_MAPS_MODULE_LOAD_ERRORS.has(exception.value) &&
  (exception.stacktrace?.frames || []).some((frame) =>
    [frame.filename, frame.abs_path].some(isGoogleMapsApiSource)
  );

const shouldIgnoreSentryEvent = (event, hint) => {
  const exceptions = event.exception?.values || [];
  const hasIgnoredSourceFrame = exceptions.some((exception) =>
    (exception.stacktrace?.frames || []).some((frame) =>
      isIgnoredErrorSource(frame.filename)
    )
  );

  return (
    hasIgnoredSourceFrame ||
    exceptions.some(isIgnoredGoogleMapsModuleError) ||
    isIgnoredErrorSource(hint?.originalException?.filename)
  );
};

const beforeSend = (event, hint) =>
  shouldIgnoreSentryEvent(event, hint) ? null : event;

module.exports = {
  beforeSend,
  SENTRY_IGNORE_ERRORS,
  SENTRY_IGNORE_ERROR_SOURCES,
};
